import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { Email } from '@/types/email';

const DB_NAME = 'winbox-email-cache';
const DB_VERSION = 1;
const EMAILS_STORE = 'emails';
const META_STORE = 'meta';
const META_KEYS = {
  LAST_SYNC_AT: 'lastSyncAt',
  LAST_SYNC_USER_ID: 'lastSyncUserId',
};

// Max emails to cache - prevents quota issues on very large accounts
const MAX_CACHED_EMAILS = 75000;

/**
 * Check if an error is a quota exceeded error
 */
function isQuotaError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'QuotaExceededError' ||
           error.name === 'NS_ERROR_DOM_QUOTA_REACHED'; // Firefox
  }
  return false;
}

/**
 * Handle quota errors by clearing cache and retrying once
 */
async function handleQuotaError(operation: () => Promise<void>): Promise<void> {
  try {
    await operation();
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn('IndexedDB quota exceeded, clearing cache and retrying...');
      await clearEmailCache();
      try {
        await operation();
      } catch (retryError) {
        console.error('Failed to write to IndexedDB after clearing cache:', retryError);
      }
    } else {
      throw error;
    }
  }
}

interface EmailCacheDB extends DBSchema {
  emails: {
    key: string; // email.id
    value: Email;
    indexes: { 'by-category': string };
  };
  meta: {
    key: string;
    value: {
      key: string;
      value: number | string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<EmailCacheDB>> | null = null;

function getDB(): Promise<IDBPDatabase<EmailCacheDB>> {
  if (!dbPromise) {
    dbPromise = openDB<EmailCacheDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create emails store with index on category
        if (!db.objectStoreNames.contains(EMAILS_STORE)) {
          const emailStore = db.createObjectStore(EMAILS_STORE, { keyPath: 'id' });
          emailStore.createIndex('by-category', 'aiAnalysis.category');
        }
        // Create meta store for sync timestamps
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Get cached emails from IndexedDB
 */
export async function getCachedEmails(): Promise<{
  emails: Email[];
  lastSyncAt: number | null;
  lastSyncUserId: string | null;
}> {
  try {
    const db = await getDB();
    const [emails, lastSyncMeta, userMeta] = await Promise.all([
      db.getAll(EMAILS_STORE),
      db.get(META_STORE, META_KEYS.LAST_SYNC_AT),
      db.get(META_STORE, META_KEYS.LAST_SYNC_USER_ID),
    ]);
    return {
      emails,
      lastSyncAt: lastSyncMeta?.value as number | null,
      lastSyncUserId: typeof userMeta?.value === 'string' ? userMeta.value : null,
    };
  } catch (error) {
    console.error('Failed to get cached emails:', error);
    return { emails: [], lastSyncAt: null, lastSyncUserId: null };
  }
}

/**
 * Store emails in IndexedDB cache
 * Enforces MAX_CACHED_EMAILS limit and handles quota errors
 */
export async function setCachedEmails(emails: Email[], syncAt: number, userId: string): Promise<void> {
  // Enforce size cap - keep most recent emails
  const emailsToCache = emails.length > MAX_CACHED_EMAILS
    ? emails.slice(0, MAX_CACHED_EMAILS)
    : emails;

  if (emails.length > MAX_CACHED_EMAILS) {
    console.warn(`Caching ${MAX_CACHED_EMAILS} of ${emails.length} emails (size limit)`);
  }

  await handleQuotaError(async () => {
    const db = await getDB();
    const tx = db.transaction([EMAILS_STORE, META_STORE], 'readwrite');

    // Clear existing emails and add new ones
    await tx.objectStore(EMAILS_STORE).clear();

    // Batch add emails in chunks to avoid transaction timeouts
    const emailStore = tx.objectStore(EMAILS_STORE);
    const chunkSize = 1000;
    for (let i = 0; i < emailsToCache.length; i += chunkSize) {
      const chunk = emailsToCache.slice(i, i + chunkSize);
      await Promise.all(chunk.map(email => emailStore.put(email)));
    }

    // Update sync metadata for active user
    await Promise.all([
      tx.objectStore(META_STORE).put({ key: META_KEYS.LAST_SYNC_AT, value: syncAt }),
      tx.objectStore(META_STORE).put({ key: META_KEYS.LAST_SYNC_USER_ID, value: userId }),
    ]);

    await tx.done;
  });
}

/**
 * Update a single email in the cache
 */
export async function updateCachedEmail(email: Email): Promise<void> {
  try {
    const db = await getDB();
    await db.put(EMAILS_STORE, email);
  } catch (error) {
    console.error('Failed to update cached email:', error);
  }
}

/**
 * Update multiple emails in the cache (for incremental sync)
 */
export async function updateCachedEmails(emails: Email[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(EMAILS_STORE, 'readwrite');
    await Promise.all(emails.map(email => tx.store.put(email)));
    await tx.done;
  } catch (error) {
    console.error('Failed to update cached emails:', error);
  }
}

/**
 * Delete emails from cache by IDs
 */
export async function deleteCachedEmails(emailIds: string[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(EMAILS_STORE, 'readwrite');
    await Promise.all(emailIds.map(id => tx.store.delete(id)));
    await tx.done;
  } catch (error) {
    console.error('Failed to delete cached emails:', error);
  }
}

/**
 * Merge incremental updates into cache
 * Handles quota errors by clearing and retrying
 */
export async function mergeIncrementalSync(
  updatedEmails: Email[],
  deletedIds: string[],
  syncAt: number,
  userId?: string
): Promise<void> {
  await handleQuotaError(async () => {
    const db = await getDB();
    const tx = db.transaction([EMAILS_STORE, META_STORE], 'readwrite');

    // Delete removed emails
    const emailStore = tx.objectStore(EMAILS_STORE);
    await Promise.all(deletedIds.map(id => emailStore.delete(id)));

    // Upsert updated emails
    await Promise.all(updatedEmails.map(email => emailStore.put(email)));

    // Update sync metadata
    await tx.objectStore(META_STORE).put({ key: META_KEYS.LAST_SYNC_AT, value: syncAt });
    if (userId) {
      await tx.objectStore(META_STORE).put({ key: META_KEYS.LAST_SYNC_USER_ID, value: userId });
    }

    await tx.done;
  });
}

/**
 * Clear the entire email cache
 */
export async function clearEmailCache(): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction([EMAILS_STORE, META_STORE], 'readwrite');
    await tx.objectStore(EMAILS_STORE).clear();
    await tx.objectStore(META_STORE).clear();
    await tx.done;
  } catch (error) {
    console.error('Failed to clear email cache:', error);
  }
}

/**
 * Get cache stats for debugging
 */
export async function getCacheStats(): Promise<{ count: number; lastSyncAt: number | null }> {
  try {
    const db = await getDB();
    const [count, meta] = await Promise.all([
      db.count(EMAILS_STORE),
      db.get(META_STORE, META_KEYS.LAST_SYNC_AT),
    ]);
    return {
      count,
      lastSyncAt: meta?.value as number | null,
    };
  } catch (error) {
    console.error('Failed to get cache stats:', error);
    return { count: 0, lastSyncAt: null };
  }
}
